import { useQueryClient } from '@tanstack/react-query';

import { Campaign } from 'domains/campaigns/types';

import useUnarchiveCampaign from './mutations/useUnarchiveCampaign';
import QueryKeys from './queries/keys';

type Props = {
    activeItem: Campaign;
    refetchTableData: () => void;
    resetActiveItem: () => void;
    setErrorMessage: (error: string) => void;
    setSuccessMessage: (message: string) => void;
};

const useUnarchiveCampaignItem = ({
    activeItem,
    refetchTableData,
    resetActiveItem,
    setErrorMessage,
    setSuccessMessage,
}: Props) => {
    const queryClient = useQueryClient();

    const onError = (error: Error) => {
        setErrorMessage(error.message);
        resetActiveItem();
    };

    const onSuccess = () => {
        setSuccessMessage(`"${activeItem?.name}" successfully unarchived.`);
        refetchTableData();
        resetActiveItem();

        queryClient.invalidateQueries(QueryKeys.CAMPAIGNS_BASE);
        queryClient.invalidateQueries(QueryKeys.ARCHIVED_CAMPAIGNS);
        queryClient.invalidateQueries(['campaigns']);
    };

    const { isLoading, mutate: unarchiveCampaign, isSuccess } = useUnarchiveCampaign({
        onError,
        onSuccess,
    });

    const getUnarchiveItemFunction = () => {
        if (!activeItem) return () => {};
        return () => unarchiveCampaign({ id: activeItem.id });
    };

    return { getUnarchiveItemFunction, isLoading, isSuccess };
};

export default useUnarchiveCampaignItem;
